'use client';

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import type { AIAvatarStatusPanelProps } from './types';
import {
  ConnectingIcon,
  MicIcon,
  ThinkingIcon,
  AnsweringIcon,
  DefaultIcon,
  PhoneOffIcon
} from './icons';
import DeviceManager from '@/components/util/deviceManager';

const AIAvatarStatusPanel: React.FC<AIAvatarStatusPanelProps> = ({
  status,
  visible,
  onEndChat,
  className = '',
  overrideStyles = {},
}) => {
  const getStatusText = (): string => {
    switch (status) {
      case 'connecting':
        return 'Connecting...';
      case 'listening':
        return 'Listening...';
      case 'answering':
        return 'Answering...';
      case 'thinking':
        return 'Thinking...';
      default:
        return 'Ready';
    }
  };

  const getStatusIcon = (className?: string): React.ReactElement => {
    switch (status) {
      case 'connecting':
        return <ConnectingIcon className={className} />;
      case 'listening':
        return <MicIcon className={className} />;
      case 'thinking':
        return <ThinkingIcon className={className} />;
      case 'answering':
        return <AnsweringIcon className={className} />;
      default:
        return <DefaultIcon className={className} />;
    }
  };

  if (!visible) {
    return null;
  }

  const isMobile = DeviceManager.isMobile();

  return (
    <Box
      className={`voice-status-panel ${className}`}
      sx={{
        position: 'fixed',
        bottom: isMobile ? 80 : 50,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        padding: '7px 12px',
        borderRadius: '10px',
        justifyContent: 'space-between',
        fontFamily: 'Inter',
        fontSize: '16px',
        fontWeight: 600,
        color: 'white',
        ...overrideStyles,
      }}
    >
      {/* Status Icon and Text */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
          }}
        >
          {getStatusIcon()}
        </Box>
        <Typography variant="body1">
          {getStatusText()}
        </Typography>
      </Box>

      {/* End Chat Button */}
      <Button
        variant="contained"
        onClick={(e) => {
          e.stopPropagation();
          onEndChat();
        }}
        sx={{
          backgroundColor: 'rgba(240, 68, 56, 1)',
          padding: '8px 16px',
          borderRadius: '6px',
          textTransform: 'none',
          minWidth: 'auto',
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          gap: 1,
          '&:hover': {
            backgroundColor: 'rgba(240, 68, 56, 0.6)',
          },
          '&:active': {
            backgroundColor: 'rgba(240, 68, 56, 0.8)',
          },
        }}
      >
        <PhoneOffIcon />
        End Chat
      </Button>
    </Box>
  );
};

export default AIAvatarStatusPanel;
